import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Briefcase, BadgeCheck, Plus, Trash2, Loader2, Smartphone, Save, Search, X, Upload, FileDown } from 'lucide-react';
import SchedulesMaster from './SchedulesMaster';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Masters() {
  return (
    <div data-testid="masters-page" className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Masters</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MasterList
          title="Departments"
          path="departments"
          icon={Briefcase}
          color="text-blue-600"
          placeholder="e.g., Sales, Marketing, Operations"
        />
        <MasterList
          title="Designations"
          path="designations"
          icon={BadgeCheck}
          color="text-emerald-600"
          placeholder="e.g., Manager, Executive, Field Staff"
        />
      </div>
      <SchedulesMaster />
      <AppVersionCard />
    </div>
  );
}

function MasterList({ title, path, icon: Icon, color, placeholder }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => (it.name || '').toLowerCase().includes(q));
  }, [items, search]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/${path}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setItems(await r.json());
    } finally { setLoading(false); }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Name'],
      [title === 'Departments' ? 'PRODUCTION' : 'Manager'],
      [title === 'Departments' ? 'SALES' : 'Field Staff'],
    ]);
    ws['!cols'] = [{ wch: 28 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title.toUpperCase());
    XLSX.writeFile(wb, `${title.toLowerCase()}-template.xlsx`);
  };

  const parseFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    let rows;
    if (ext === 'csv') {
      const text = await file.text();
      const cleaned = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
      const wb = XLSX.read(cleaned, { type: 'string' });
      rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
    } else {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
    }
    // Accept "Name", "name", or first column.
    return rows
      .map(r => {
        const key = Object.keys(r).find(k => String(k).trim().toLowerCase() === 'name') || Object.keys(r)[0];
        const name = String(r[key] ?? '').trim();
        return { name };
      })
      .filter(r => r.name);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportMsg(''); setErr('');
    try {
      const rows = await parseFile(file);
      if (!rows.length) {
        setImportMsg('File empty or no Name column found.');
        return;
      }
      if (!window.confirm(
        `Import ${rows.length} ${title.toLowerCase()}?\n\n` +
        `• Existing names (case-insensitive) will be SKIPPED.\n` +
        `• New names will be created.`
      )) return;
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/${path}/bulk-import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setImportMsg(d.detail || `Import failed (${r.status})`); return; }
      setImportMsg(`Created ${d.created} · Skipped ${d.skipped_existing} duplicates · ${d.skipped_invalid} blank rows.`);
      fetchAll();
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const name = input.trim();
    if (!name) return;
    setErr('');
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/${path}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const txt = await r.text();
      let data = {};
      try { data = txt ? JSON.parse(txt) : {}; } catch {}
      if (!r.ok) {
        setErr(data.detail || `Failed (${r.status})`);
        return;
      }
      setInput('');
      fetchAll();
    } finally { setBusy(false); }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/${path}/${item.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!r.ok) {
      const txt = await r.text();
      try { const d = JSON.parse(txt); alert(d.detail || 'Delete failed'); } catch { alert('Delete failed'); }
      return;
    }
    fetchAll();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4" data-testid={`master-${path}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className={color} />
        <h2 className="font-semibold">{title}</h2>
        <span className="text-xs text-gray-500" data-testid={`master-${path}-count`}>
          {search ? `${filtered.length}/${items.length}` : items.length}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={downloadTemplate}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded"
            data-testid={`master-${path}-template`}
            title="Download Excel template"
          >
            <FileDown size={12} /> Template
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 rounded disabled:opacity-50"
            data-testid={`master-${path}-import-btn`}
            title="Import from Excel / CSV"
          >
            {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Import
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            data-testid={`master-${path}-file`}
            onChange={handleFile}
          />
        </div>
      </div>

      {importMsg && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mb-2" data-testid={`master-${path}-import-msg`}>
          {importMsg}
        </p>
      )}

      <form onSubmit={add} className="flex gap-2 mb-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border rounded-lg text-sm"
          data-testid={`master-${path}-input`}
          maxLength={120}
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg flex items-center gap-1 text-sm"
          data-testid={`master-${path}-add`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add
        </button>
      </form>

      {err && <p className="text-xs text-red-600 mb-2" data-testid={`master-${path}-err`}>{err}</p>}

      {/* Search box (visible only when there are >5 items) */}
      {items.length > 5 && (
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}…`}
            className="w-full pl-8 pr-8 py-1.5 border rounded-lg text-xs"
            data-testid={`master-${path}-search`}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid={`master-${path}-search-clear`}
            >
              <X size={12} />
            </button>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4">No {title.toLowerCase()} yet.</p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4" data-testid={`master-${path}-empty`}>No matches for "{search}".</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {filtered.map(it => (
            <li key={it.id} className="py-2 flex items-center justify-between" data-testid={`master-${path}-row-${it.id}`}>
              <span className="text-sm">{it.name}</span>
              <button
                onClick={() => del(it)}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                data-testid={`master-${path}-del-${it.id}`}
                title="Delete"
              ><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AppVersionCard() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const fetchCurrent = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/app-version`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (r.ok) setForm(await r.json());
        else setForm({ version_code: 20000, version_name: '2.0.0', apk_url: '/carloo-tex.apk', mandatory: false, changelog: '' });
      } finally { setLoading(false); }
    };
    fetchCurrent();
  }, []);

  const save = async () => {
    if (!form) return;
    setSaving(true); setMsg('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/app-version`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version_code: parseInt(form.version_code, 10) || 1,
          version_name: form.version_name,
          apk_url: form.apk_url,
          mandatory: !!form.mandatory,
          changelog: form.changelog || '',
        }),
      });
      const txt = await r.text();
      let data = {}; try { data = txt ? JSON.parse(txt) : {}; } catch {}
      if (!r.ok) setMsg(data.detail || `Save failed (${r.status})`);
      else setMsg('Saved. Native apps will see the update on their next check (within 6h, or on next cold start).');
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4" data-testid="master-app-version">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone size={18} className="text-purple-600" />
        <h2 className="font-semibold">App Update (Android APK)</h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Bump <strong>version_code</strong> every release. Native apps compare installed vs. latest and prompt users to install.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <label className="text-xs">
          <span className="text-gray-600">Version Code (integer)</span>
          <input
            type="number" min={1} value={form.version_code ?? 1}
            onChange={e => setForm({ ...form, version_code: e.target.value })}
            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            data-testid="app-version-code"
          />
        </label>
        <label className="text-xs">
          <span className="text-gray-600">Version Name (display)</span>
          <input
            type="text" value={form.version_name ?? ''}
            onChange={e => setForm({ ...form, version_name: e.target.value })}
            placeholder="1.1.0"
            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            data-testid="app-version-name"
          />
        </label>
        <label className="text-xs sm:col-span-2">
          <span className="text-gray-600">APK URL (relative or absolute)</span>
          <input
            type="text" value={form.apk_url ?? ''}
            onChange={e => setForm({ ...form, apk_url: e.target.value })}
            placeholder="/carloo-tex.apk"
            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            data-testid="app-version-url"
          />
        </label>
        <label className="text-xs sm:col-span-2">
          <span className="text-gray-600">What's new (changelog)</span>
          <textarea
            value={form.changelog ?? ''}
            onChange={e => setForm({ ...form, changelog: e.target.value })}
            rows={3}
            placeholder="- Bug fixes&#10;- New map picker"
            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            data-testid="app-version-changelog"
          />
        </label>
        <label className="flex items-center gap-2 text-xs sm:col-span-2">
          <input
            type="checkbox" checked={!!form.mandatory}
            onChange={e => setForm({ ...form, mandatory: e.target.checked })}
            data-testid="app-version-mandatory"
          />
          <span className="text-gray-700">Mandatory (blocks the app until installed)</span>
        </label>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg flex items-center gap-1 text-sm"
          data-testid="app-version-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
        {msg && <span className={`text-xs ${msg.startsWith('Saved') ? 'text-emerald-600' : 'text-red-600'}`} data-testid="app-version-msg">{msg}</span>}
      </div>
    </div>
  );
}
